import { useState, useEffect } from 'react';

export function useLocalStorage<T>(
  key: string, 
  initialValue: T,
  fallback?: T
): [T, (value: T) => void, boolean] {
  const [storedValue, setStoredValue] = useState<T>(initialValue);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    try {
      // Check if localStorage is available
      if (typeof window === 'undefined' || !window.localStorage) {
        setIsLoading(false);
        return;
      }

      const item = window.localStorage.getItem(key);
      if (item) {
        const parsed = JSON.parse(item);
        setStoredValue(parsed);
      } else if (fallback !== undefined) {
        setStoredValue(fallback);
      }
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error);
      if (fallback !== undefined) {
        setStoredValue(fallback);
      }
    } finally {
      setIsLoading(false);
    }
  }, [key, fallback]);

  const setValue = (value: T) => {
    try {
      setStoredValue(value);
      
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem(key, JSON.stringify(value));
      }
    } catch (error) {
      console.warn(`Error setting localStorage key "${key}":`, error);
    }
  };

  return [storedValue, setValue, isLoading];
}

export function useSessionStorage<T>(
  key: string, 
  initialValue: T,
  fallback?: T
): [T, (value: T) => void, boolean] {
  const [storedValue, setStoredValue] = useState<T>(initialValue);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    try {
      if (typeof window === 'undefined' || !window.sessionStorage) {
        setIsLoading(false);
        return;
      }

      const item = window.sessionStorage.getItem(key);
      if (item) {
        const parsed = JSON.parse(item);
        setStoredValue(parsed);
      } else if (fallback !== undefined) {
        setStoredValue(fallback);
      }
    } catch (error) {
      console.warn(`Error reading sessionStorage key "${key}":`, error);
      if (fallback !== undefined) {
        setStoredValue(fallback);
      }
    } finally {
      setIsLoading(false);
    }
  }, [key, fallback]);

  const setValue = (value: T) => {
    try {
      setStoredValue(value);
      
      if (typeof window !== 'undefined' && window.sessionStorage) {
        window.sessionStorage.setItem(key, JSON.stringify(value));
      }
    } catch (error) {
      console.warn(`Error setting sessionStorage key "${key}":`, error);
    }
  };

  return [storedValue, setValue, isLoading];
}